import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { Link, useLocation } from "react-router-dom";

export function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  // On non-home pages, always treat as "dark" nav (no white-on-scroll)
  const isDark = !isHome || !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "/#home" },
    { name: "Services", href: "/#services" },
    { name: "Projects", href: "/projects" },
    { name: "Contact", href: "/#contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        !isHome
          ? "bg-[#1a3a6b]"
          : scrolled
          ? "bg-white/98 border-b border-blue-100 shadow-lg backdrop-blur"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 flex h-16 md:h-20 items-center justify-between overflow-visible">
        <Link to="/#home" className="flex h-full shrink-0 items-center overflow-visible">
          <Logo white={isDark} className="h-24 w-24 md:h-32 md:w-32" />
        </Link>

        {/* Desktop Nav */}
        <nav
          className={`hidden md:flex items-center gap-8 text-sm font-semibold transition-colors ${!isDark ? "text-slate-700" : "text-white/90"
            }`}
        >
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              className="hover:text-blue-600 transition-colors"
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* Mobile toggle */}
        <button
          className={`relative z-60 md:hidden rounded-full p-2 transition-colors ${
            !isDark
              ? "text-slate-700 hover:bg-slate-100"
              : "text-white hover:bg-white/10"
          }`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile dropdown */}
      <div
        className={`absolute right-5 top-17 z-55 md:hidden origin-top-right transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open
            ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
            : "pointer-events-none -translate-y-2 scale-95 opacity-0"
        }`}
      >
        <div className="min-w-44 rounded-2xl border border-white/20 bg-slate-950/65 px-4 py-4 text-sm font-semibold text-white shadow-2xl shadow-slate-950/25 backdrop-blur-md">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              onClick={() => setOpen(false)}
              className="block rounded-lg px-3 py-2 transition-colors hover:bg-white/10"
            >
              {link.name}
            </Link>
          ))}
          <a
            href="tel:5042872940"
            className="mt-2 block rounded-lg px-3 py-2 font-bold text-blue-200 transition-colors hover:bg-white/10 hover:text-white"
          >
            (504) 287-2940
          </a>
        </div>
      </div>
    </header>
  );
}
